import { IDateRangeComposite } from '@/interfaces/general/IDateRange.ts';
import { ITableDataConfig } from '../table/ITableDataConfig.ts';
import { IDashboardPresetItemInfo } from '../dashboard/IDashboardPresetItemInfo.ts';

export interface IAxisPosition {
    label: string;
    value: number;
    edited: boolean;
}

export interface IAxisLimits {
    min: number | null;
    max: number | null;
    /**
     * User-entered min/max constraints (used when dynamicExpand is enabled).
     * Kept separately from min/max because min/max may be auto-calculated.
     */
    userMin?: number | null;
    userMax?: number | null;
    minPadding: number | null;
    maxPadding: number | null;
    /**
     * When true, the chart may auto-contract min/max based on available data
     * (optionally within userMin/userMax constraints).
     */
    dynamicExpand?: boolean;
    title: { text: string | string[] };
}

export interface IModuleChartConfig {
    data: ITableDataConfig;
    info: IDashboardPresetItemInfo;
}
export interface IModuleChartRequestDataSeries {
    Id: string;
    CalculatorIdArray: Array<string> | null;
    Colour: string | null;
    ChartType: string | null;
    Name: string;
    Multiplier: string | null;
    SkipAddingSeriesIfNoData?: boolean;
}

export interface ICombinedAggregateOptions {
    conversionOptions?: {
        newCurrency?: string;
        currencyOverride?: string;
    };
    granularity?: string | null;
    type?: string | null;
    cumulative?: boolean;
    delta?: number | null;
}

// TODO - IModuleChartRequestPayload should be used instead of IModuleChartRequestData
export interface IModuleChartRequestData {
    series: IModuleChartRequestDataSeries;
    noHistorics: boolean;
    missingCalculatorOption?: string | null;
    aggregateOptions?: ICombinedAggregateOptions | null;
    dates?: Array<IDateRangeComposite>;
}

export interface IModuleChartRequest {
    [key: string]: Array<IModuleChartRequestData>;
}

export interface IModuleChartConfigPayload {
    optionId: string;
    country: string;
}

export interface IModulePlantChartMultipleConfigPayload {
    chartType: string;
    country?: string;
    optionsIdArray: Array<string>;
    seriesId: string;
}

export interface IModulePlantChartConfigPayload {
    chartType: string;
    country?: string;
    plantId: string;
}

export interface IModuleChartRequestPayload extends IModuleChartRequestData {
    dates?: Array<IDateRangeComposite>;
    timezoneId?: string;
}

export interface IModuleChartSeriesData {
    [key: string]: Array<Array<number>>;
}

export interface IModuleChartRequestResult {
    historicalData: IModuleChartSeriesData;
    realTimeData: IModuleChartSeriesData;
}

export interface IModuleChartRequestPromise {
    requestObjects: Array<IModuleChartRequestPayload>;
    requestApiCall: Promise<IModuleChartRequestResult>;
}

export interface IModuleChartMetricsPayload {
    data: Array<IModuleChartRequestPayload>;
    hiddenHeadings: Array<string>;
    seriesWithData: Array<string>;
}

export interface IModuleChartMetrics {
    id: string;
    min: string;
    max: string;
    average: string;
    sum: string;
    latest: string;
    standardDeviation: string;
    variance: string;
}

export interface IModuleChartXAxisValues {
    values: string;
    verticalLine: '1min' | '5min' | 'period';
}

export interface IMapChartSeries {
    dates: Array<string | never>;
    series: {
        chartType: string;
        country: string;
        optionsId: string | null;
        seriesId: string;
        isPlant: boolean;
    };
}

export interface IMapSeriesConfigPayload {
    id: string;
    chartSeries: Array<IMapChartSeries>;
}

export enum ViewMode {
    Chart = 'Chart',
    Table = 'Table',
    Metrics = 'Metrics',
}
